import React, { useState } from "react";
import useSWR from "swr";
import api from "@/lib/api";
import { fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputES } from "@/components/ui/DateInputES";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { Plus, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { TASK_PRIORITY } from "@/lib/labels";

const fetcher = (u) => api.get(u).then((r) => r.data);
const empty = { tour_id: "", title: "", due_date: "", priority: "medium", status: "todo" };

export default function Tasks() {
  const { data: tours } = useSWR("/tours", fetcher);
  const { data: tasks, mutate } = useSWR("/tasks", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const submit = async () => {
    try {
      await api.post("/tasks", form);
      toast.success("Tarea creada");
      setForm(empty); setOpen(false); mutate();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "todo" : "done" });
    mutate();
  };

  const tourName = (id) => tours?.find((t) => t.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Tareas & Alertas</h1><p className="text-slate-500 mt-1">Actividades pendientes en todos los tours.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-task-btn" className="bg-[#026FCB] hover:bg-[#0358a5]"><Plus size={16} className="mr-2" />Nueva tarea</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nueva tarea</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} /></div>
              <div className="col-span-2"><Label>Tour</Label>
                <Select value={form.tour_id} onValueChange={(v) => setForm({...form, tour_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Sin tour" /></SelectTrigger>
                  <SelectContent>{(tours || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Vencimiento</Label><DateInputES value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} /></div>
              <div><Label>Prioridad</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["low","medium","high","urgent"].map((p) => <SelectItem key={p} value={p}>{TASK_PRIORITY[p]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={submit}>Crear</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden"><CardContent className="p-0 overflow-x-auto">
        <ul className="divide-y divide-slate-100 dark:divide-[#25334D]/50">
          {(tasks || []).map((t) => (
            <li key={t.id} data-testid={`task-row-${t.id}`} className="p-4 flex items-center gap-3">
              <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`}>
                {t.status === "done" ? <CheckCircle2 size={20} className="text-emerald-600" /> : <Circle size={20} className="text-slate-400" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${t.status === "done" ? "line-through text-slate-400" : ""}`}>{t.title}</div>
                <div className="text-xs text-slate-500 mono mt-0.5">{tourName(t.tour_id)} · vence {fmtDate(t.due_date)}</div>
              </div>
              <StatusBadge value={t.priority} />
            </li>
          ))}
          {(!tasks || tasks.length === 0) && <li className="p-10 text-center text-slate-500">Sin tareas.</li>}
        </ul>
      </CardContent></Card>
    </div>
  );
}
